import { useEffect, useRef, useState } from 'react';

/**
 * Devuelve `[ref, visible]` — `visible` pasa a `true` la primera vez que el
 * elemento entra en el viewport y queda en `true` para siempre (one-shot).
 * Sirve para disparar animaciones de entrada sin re-animar al hacer scroll.
 *
 * `rootMargin` permite anticipar la animación (ej. `-50px` para que dispare
 * cuando el elemento todavía está 50px abajo del fold visible).
 */
export function useInViewport<T extends Element>(
  options?: { rootMargin?: string; threshold?: number },
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    // Fallback: navegadores muy viejos sin IntersectionObserver — asumimos visible.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: options?.rootMargin ?? '-40px 0px',
        threshold: options?.threshold ?? 0.1,
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, options?.rootMargin, options?.threshold]);

  return [ref, visible];
}
